/**
 * THE FILE — the board. Work laid out by the state it is in, as columns.
 *
 * The columns are whatever the caller is handed, in the order it is handed
 * them, with the words it is handed: this module does not know how many there
 * are or what they are called. Four tracks at a desk, two on a tablet, one on
 * a phone — and a column that does not arrive leaves no gap, because an empty
 * rail is noise.
 *
 * THE HEADS LINE UP. Each column is a subgrid of the board, so a long hint in
 * one column pushes every column's first card down by the same amount, and
 * the eye can run along the tops of the cards as one line. The rows are
 * `auto`, never `1fr`: flexible rows are equalised across the whole grid, and
 * a stacked phone board would pad every short column to the longest one.
 */
import { clsx } from "clsx";
import type { ReactNode } from "react";

export interface BoardGridProps {
  children: ReactNode;
  className?: string | undefined;
}

export function BoardGrid({ children, className }: BoardGridProps) {
  return (
    <div
      className={clsx(
        "grid grid-cols-1 gap-x-5 gap-y-9 sm:grid-cols-2 xl:grid-cols-4",
        className
      )}
    >
      {children}
    </div>
  );
}

export interface BoardColumnProps {
  /** The id the heading carries, so the column is named by it. */
  id: string;
  label: ReactNode;
  /** How many are in the column — set in the figure face. */
  count?: number | undefined;
  /** What the column means, in the product's own words. Never a tooltip. */
  hint?: ReactNode;
  children: ReactNode;
}

export function BoardColumn({ id, label, count, hint, children }: BoardColumnProps) {
  // The single track is minmax(0, 1fr), not the implicit `auto`: an auto
  // track grows to the unwrapped width of a clipped name and pushes the
  // cards out over the next column.
  return (
    <section aria-labelledby={id} className="row-span-2 grid min-w-0 grid-cols-[minmax(0,1fr)] grid-rows-subgrid gap-y-3">
      <div className="border-b border-rule pb-2.5">
        <div className="flex items-baseline justify-between gap-3">
          <h3 id={id} className="text-[13px] font-semibold text-ink">{label}</h3>
          {typeof count === "number" && <span className="font-mono text-[12px] text-stone">{count}</span>}
        </div>
        {hint && <p className="mt-0.5 text-[12px] leading-snug text-stone">{hint}</p>}
      </div>
      <ul className="flex flex-col gap-2.5">{children}</ul>
    </section>
  );
}

/**
 * The face of one card on the board. A card is a separable object — one piece
 * of work — so it has an edge and no radius. The link itself is the caller's
 * (a router link), so the board lends it only its face.
 */
export const BOARD_CARD =
  "block border border-rule-soft bg-raised px-3.5 py-3 transition-colors duration-150 hover:border-rule hover:bg-canvas";
